import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Container, Row, Col, Badge, Button, Table } from "react-bootstrap";
import { useMediaQuery } from 'react-responsive';

import useUserStore from "../stores/User";

import { formatDate, formatDateTime } from "../utils/Dates";
import { getDownloadsByUser, getDatabaseRecordsByIndexer } from "../utils/Database";
import { getAllUserFavorites, saveFavorite, removeFavorite } from "../utils/Favorites";
import { deleteRequest, getUserRequests } from "../utils/Requests";
import { showToast } from "../utils/Toasts";
import { sortScripts } from '../utils/SortScripts';
import { linkPatreon, unlinkPatreon, getPatreonUser, isPatreonLinkingAllowed } from "../utils/Patreon";
import { getUserInfo, deleteAccount } from "../utils/UserUtil";
import { userHasElevatedAccess, userHasBenefit } from "../utils/Benefits";
import supabase from "../utils/Supabase";

import { LoadingRing } from "../components/LoadingRing";
import { LoadingText } from "../components/LoadingText";
import AccountNav from "../components/AccountNav";
import { ConfirmationModal, DeleteAccountModal } from "../components/Confirmation";
import { PatreonLogo } from "../components/PatreonLogo";

const PATREON_CLIENT_ID = process.env.REACT_APP_PATREON_CLIENT_ID || null;

function AccountPage() {
    const navigate = useNavigate();
    const isSmall = useMediaQuery({ query: '(max-width: 576px)' });
    const isLarge = useMediaQuery({ query: '(min-width: 992px)' });
    const userData = useUserStore((state) => state.data);
    const setUserData = useUserStore((state) => state.setUserData);
    const userIsLoading = useUserStore((state) => state.loading);

    const [userDownloads, setUserDownloads] = useState([]);
    const [isLoadingDownloads, setIsLoadingDownloads] = useState(true);

    const initialFavorites = useRef([])
    const [userFavorites, setUserFavorites] = useState([]);
    const [isLoadingFavorites, setIsLoadingFavorites] = useState(true);

    const [userRequests, setUserRequests] = useState([]);
    const [addedRequests, setAddedRequests] = useState([]);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [isLoadingRequests, setIsLoadingRequests] = useState(true);

    const [userIndexed, setUserIndexed] = useState([]);
    const [isLoadingIndexed, setIsLoadingIndexed] = useState(true);

    const [activeTab, setActiveTab] = useState("favorites");

    const [confirmationMessage, setConfirmationMessage] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [onDeleteConfirm, setOnDeleteConfirm] = useState(() => () => { });
    const [onDeleteHide, setOnDeleteHide] = useState(() => () => { });

    const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);

    const [isUpdatingPatreon, setIsUpdatingPatreon] = useState(false);
    const [patreonUserLoading, setPatreonUserLoading] = useState(false);
    const [patreonLinked, setPatreonLinked] = useState(userData?.patreon_linked || false);
    const [patreonUser, setPatreonUser] = useState(null);
    const [patreonMessage, setPatreonMessage] = useState('');
    const [membershipStatus, setMembershipStatus] = useState('Never Subscribed');

    useEffect(() => {
        const fetchDownloads = async () => {
            if (userData) {
                const downloads = await getDownloadsByUser(userData?.user_id);
                const uniqueDownloads = downloads.filter((download, index, self) =>
                    index === self.findIndex((d) => d.script_id === download.script_id)
                );

                if (uniqueDownloads.length > 0) {
                    setUserDownloads(uniqueDownloads);
                }
            }
            setIsLoadingDownloads(false);
        };
        
        fetchDownloads();

        const urlParams = new URLSearchParams(window.location.search);
        const patreonStatus = urlParams.get('patreon_status');

        if (patreonStatus) {
            setIsUpdatingPatreon(false);
            if (patreonStatus === 'success') {
                setUserData({ ...userData, patreon_linked: true, patreon_ever_linked: true });
                setPatreonLinked(true);
                showToast('success', 'Patreon account linked successfully!');
            } else if (patreonStatus === 'cancelled') {
                setPatreonLinked(false);
                showToast('info', 'Patreon linking cancelled');
            } else if (patreonStatus === 'error') {
                const rawReason = urlParams.get('reason');
                const reason = rawReason === 'no_active_session' ? 'no active session found'
                    : rawReason === 'invalid_state' ? 'there was a problem validating the authorization request'
                    : rawReason === 'missing_code' ? 'the authorization response was missing required information'
                    : rawReason === 'token_exchange_failed' ? 'there was an issue getting a token from Patreon'
                    : rawReason === 'invalid_token_response' ? 'the response from Patreon was invalid'
                    : rawReason === 'token_storage_failed' ? 'there was an issue saving your Patreon information'
                    : rawReason === 'user_already_linked' ? 'this Patreon account is already linked to another user'
                    : 'there was an unexpected exception';
                setPatreonLinked(false);
                showToast('error', `Sorry, ${reason}. Please try again.`);
            } else {
                const reason = urlParams.get('reason') || 'unknown error';
                setPatreonLinked(false);
                showToast('error', `Failed to link Patreon: ${reason}`);
            }

            // Clean up URL
            navigate('/account', { replace: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData]);

    useEffect(() => {
        const fetchFavorites = async () => {
            if (userData) {
                const favorites = await getAllUserFavorites(userData?.user_id);
                initialFavorites.current = favorites || [];
                setUserFavorites(favorites || []);
            }
            setIsLoadingFavorites(false);
        }

        // Favorites tab only loads when this account has the scriptFavoriting feature
        if (userData && userHasBenefit(userData, "scriptFavoriting") && activeTab === "favorites") {
            setIsLoadingFavorites(true);
            fetchFavorites();
        }

        const fetchRequests = async () => {
            if (userData) {
                const requests = await getUserRequests(userData?.user_id);
                setUserRequests(requests || []);
            }
            setIsLoadingRequests(false);
        }

        if (userData && activeTab === "requests") {
            setIsLoadingRequests(true);
            fetchRequests();
        }

        const fetchIndexed = async () => {
            if (userData) {
                const indexed = await getDatabaseRecordsByIndexer(userData?.user_id);

                if (indexed.length > 0) {
                    setUserIndexed(indexed);
                }
                setIsLoadingIndexed(false);
            }
        };

        if (userData && userData?.isIndexer && activeTab === "indexed") {
            setIsLoadingIndexed(true);
            fetchIndexed();
        }
    }, [userData, activeTab]);

    useEffect(() => {
        const added = userRequests.filter(r => r.added_to_database);
        const sortAdded = sortScripts(added, { key: 'files.Date Added', direction: 'desc' });
        setAddedRequests(sortAdded);

        setPendingRequests(userRequests.filter(r => !r.added_to_database));
    }, [userRequests]);

    useEffect(() => {
        if (patreonLinked) {
            setPatreonUserLoading(true);
            // Fetch Patreon user data when linked
            const fetchPatreonUser = async () => {
                const patreonData = await getPatreonUser(userData.user_id);
                setPatreonUser(patreonData);
                if (patreonData?.patron_status === 'active_patron') {
                    setMembershipStatus("Active Patron");
                } else if (patreonData?.patron_status === 'former_patron') {
                    setMembershipStatus("Former Patron");
                } else {
                    setMembershipStatus("Never Subscribed");
                }
                setPatreonUserLoading(false);
            };
            fetchPatreonUser();
        } else {
            setPatreonUser(null);
            setPatreonUserLoading(false);
        }
    }, [patreonLinked, userData?.user_id]);

    useEffect(() => {
        if (patreonUser) {
            const hasExtras = userHasElevatedAccess(userData);

            if (patreonUser?.valid_tokens === false) {
                setPatreonMessage("We can't refresh your Patreon connection, so your Patreon benefits are paused. Unlink and then link your account again to restore them.");
                return;
            }

            if (patreonUser?.patron_status === 'active_patron') {
                if (hasExtras) {
                    setPatreonMessage("Thank you for supporting us! You have access to exclusive features.");
                } else {
                    setPatreonMessage("Thank you for your support, but you don't have access to exclusive features. Consider upgrading!");
                }
            } else if (patreonUser?.patron_status === 'former_patron') {
                setPatreonMessage("Thank you for your past support. Consider reactivating your membership!");
            } else {
                setPatreonMessage("Consider supporting us on Patreon to gain access to exclusive features!");
            }
        }
    }, [patreonUser, userData]);

    return userIsLoading? (
        <LoadingRing />
    ) : (
        <div className="hive-container d-flex">
            <div className="d-flex justify-content-between w-100">
                <Container fluid className="mx-sm-5 px-3 p-0 pt-sm-2 pb-sm-4">
                    <Row xs={1} lg={2}>
                        <Col xs={12} lg={6} className="mb-4">
                            <Row>
                                <Col>
                                    <h1 className="fs-3">Profile</h1>
                                </Col>
                            </Row>

                            <Row xs={1} lg={2}>
                                <Col xs={12} lg={6}>
                                    <h3 className="fs-5 mb-0">Email</h3>
                                    <p className="text-break">{userData?.email}</p>
                                </Col>
                                <Col xs={12} lg={6}>
                                    <h3 className="fs-5 mb-0">Account Created</h3>
                                    <p className="text-break">{formatDateTime(userData?.created_at)}</p>
                                </Col>
                            </Row>

                            <Row>
                                <Col>
                                    <h3 className="fs-5 mb-0">Roles</h3>
                                    <p className="text-break">
                                        <Badge bg="primary" text="dark" className="me-1">Member</Badge>
                                        {userData?.isApprover && (<Badge bg="primary" text="dark" className="me-1">Admin</Badge>)}
                                        {userData?.isIndexer && (<Badge bg="primary" text="dark" className="me-1">Indexer</Badge>)}
                                        {userData?.isCurator && (<Badge bg="primary" text="dark" className="me-1">Curator</Badge>)}
                                        {userData?.isTagger && (<Badge bg="primary" text="dark" className="me-1">Tagger</Badge>)}
                                        {userData?.isFeatureAdmin && (<Badge bg="primary" text="dark" className="me-1">Feature Admin</Badge>)}
                                        {/* Account badges come from resolved benefits, not the old VIP/Platinum flags. */}
                                        {userHasElevatedAccess(userData) && (<Badge bg="primary" text="dark" className="me-1">VIP</Badge>)}
                                        {userHasBenefit(userData, "platinumAccess") && (<Badge bg="primary" text="dark" className="me-1">Platinum</Badge>)}
                                    </p>
                                </Col>
                            </Row>

                            {PATREON_CLIENT_ID && (
                                <Row>
                                    <Col>
                                        <h2 className="fs-3 mt-2">Patreon</h2>
                                        {!patreonLinked ? (
                                            <>
                                                <p>Link your Patreon account to activate VIP features.</p>
                                                {/* Temp allowlist: everyone else sees "Coming Soon" until linking is open */}
                                                {isPatreonLinkingAllowed( userData?.email ) ? (
                                                    <Button
                                                        variant="primary"
                                                        size="sm"
                                                        className="mb-2"
                                                        onClick={async () => {
                                                            setIsUpdatingPatreon(true);
                                                            const started = await linkPatreon();
                                                            if (!started) {
                                                                setIsUpdatingPatreon(false);
                                                            }
                                                        }}
                                                    >
                                                            {isUpdatingPatreon ? (
                                                                <LoadingText text="Linking..." className="mb-0" />
                                                            ) : (
                                                                <span><PatreonLogo className="me-2"/>Link Patreon</span>
                                                            )}
                                                    </Button>
                                                ) : (
                                                    <p>*** Coming Soon! ***</p>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <Col className="mb-4">
                                                    <Row xs={1} lg={2}>
                                                        <Col xs={12} lg={6}>
                                                            <h3 className="fs-5 mb-0">Linked Email</h3>
                                                            {patreonUserLoading ? (
                                                                <p className="text-break"><LoadingText text="Getting email..." className="mb-0" /></p>
                                                            ) : (
                                                                <p className="text-break">{patreonUser?.patron_email ?? 'Invalid Email'}</p>
                                                            )}
                                                        </Col>
                                                        <Col xs={12} lg={6}>
                                                            <h3 className="fs-5 mb-0">Membership</h3>
                                                            {patreonUserLoading ? (
                                                                <p className="text-break"><LoadingText text="Getting membership..." className="mb-0" /></p>
                                                            ) : (
                                                                <p className="text-break">
                                                                    {patreonUser?.valid_tokens === false ? (
                                                                        <Badge pill bg="danger">Needs to unlink/relink.</Badge>
                                                                    ) : (
                                                                        <>
                                                                            <Badge pill bg={membershipStatus === "Active Patron" ? "success" : "secondary"} className="me-1">{patreonUser?.tier_title ?? "No Tier"}</Badge>
                                                                            <Badge pill bg={membershipStatus === "Active Patron" ? "success" : "secondary"} className="me-1">{membershipStatus ?? "Never Subscribed"}</Badge>
                                                                        </>
                                                                    )}
                                                                </p>
                                                            )}
                                                        </Col>
                                                    </Row>

                                                    {patreonMessage && (
                                                        <Row>
                                                            <p style={{ color: patreonUser?.valid_tokens === false ? "red" : "inherit" }}>{patreonMessage}</p>
                                                        </Row>
                                                    )}

                                                    <Row>
                                                        <Col>
                                                            <Button
                                                                variant={patreonUser?.valid_tokens === false ? "primary" : "secondary"}
                                                                size="sm"
                                                                onClick={async () => {
                                                                    setIsUpdatingPatreon(true);
                                                                    const unlinkResult = await unlinkPatreon(userData?.user_id);
                                                                    if (unlinkResult && userData?.user_id) {
                                                                        const refreshed = await getUserInfo(userData.user_id);
                                                                        if (refreshed) {
                                                                            setUserData(refreshed);
                                                                        } else {
                                                                            setUserData({
                                                                                ...userData,
                                                                                patreon_linked: false,
                                                                                patreon_tier_id: null,
                                                                                patreon_ever_linked: true,
                                                                            });
                                                                        }
                                                                    }
                                                                    setIsUpdatingPatreon(false);
                                                                    setPatreonUser(null);
                                                                    setPatreonLinked(false);
                                                                }}
                                                            >
                                                                        {isUpdatingPatreon ? (
                                                                            <LoadingText text="Unlinking..." className="mb-0" />
                                                                        ) : (
                                                                            <span><PatreonLogo className="me-2" />Unlink Patreon</span>
                                                                        )}
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            </>
                                        )}
                                    </Col>
                                </Row>
                            )}

                            <Row>
                                <Col>
                                    <h2 className="fs-3 mt-2">Support</h2>
                                    <p>Reach out to <a href="mailto:support@scripthive.com">support@scripthive.com</a> to report an issue or ask for assistance.</p>
                                </Col>
                            </Row>

                            <Row>
                                <Col>
                                    <h2 className="fs-3 mt-2">Danger Zone</h2>
                                    <p>Permanently delete your account and all associated data. This cannot be undone.</p>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => setShowDeleteAccountModal(true)}
                                    >
                                        Delete Account
                                    </Button>
                                </Col>
                            </Row>
                        </Col>
                        <Col xs={12} lg={6} className="mb-4">
                            <AccountNav setActiveTab={setActiveTab} />
                            <Row>
                                {activeTab === "favorites" && (
                                    <>
                                    {/* Without scriptFavoriting, show the upgrade prompt instead of the list */}
                                    {userHasBenefit(userData, "scriptFavoriting") ? (
                                        <>
                                            {initialFavorites.current.length > 0 ? (
                                                <div className="script-table overflow-auto" style={isLarge ? { maxHeight: 'calc(100vh - 210px)' } : {}}>
                                                    <Table striped hover size="sm">
                                                        <thead className="sticky-top">
                                                            <tr>
                                                                <th style={{ width: 30 }} className="text-center">
                                                                    <i className="bi bi-star" aria-label="Favorite" title="Favorite"></i>
                                                                </th>
                                                                <th>Script Title</th>
                                                                {!isSmall && (
                                                                    <>
                                                                        <th>Writer(s)</th>
                                                                        <th>Draft Date</th>
                                                                    </>
                                                                )}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {initialFavorites.current.map((fav) => {
                                                                if(!fav.files) return null; // Safety check
                                                                const stillInFavorites = userFavorites.find(item => item.file_id === fav.files.file_id);
                                                                return (
                                                                    <tr key={fav.files.file_id}>
                                                                        <td className="h6 text-center" key={"fav_" + fav.files.file_id}>
                                                                            <button
                                                                                id={"star_" + fav.files.file_id}
                                                                                className="icon-only-button"
                                                                                onClick={() => {
                                                                                    if (stillInFavorites) {
                                                                                        removeFavorite(fav.files.file_id, userData?.user_id).then((success) => {
                                                                                            if (success) {
                                                                                                getAllUserFavorites(userData?.user_id).then((favorites) => {
                                                                                                    setUserFavorites(favorites || []);
                                                                                                    showToast("info", `Removed ${fav.files["Script Title"]} from Favorites`, 1000);
                                                                                                });
                                                                                            } else {
                                                                                                showToast("error", `Unable to remove ${fav.files["Script Title"]} from Favorites`, 1000);
                                                                                            }
                                                                                        });
                                                                                    } else {
                                                                                        saveFavorite(fav.files.file_id, userData?.user_id).then((success) => {
                                                                                            if (success) {
                                                                                                getAllUserFavorites(userData?.user_id).then((favorites) => {
                                                                                                    setUserFavorites(favorites || []);
                                                                                                    showToast("success", `Added ${fav.files["Script Title"]} to Favorites`, 1000);
                                                                                                });
                                                                                            } else {
                                                                                                showToast("error", `Unable to add ${fav.files["Script Title"]} to Favorites`, 1000);
                                                                                            }
                                                                                        });
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <i className={`bi bi-star-fill ${ stillInFavorites ? "selected" : ""}`} aria-label="Favorite" title="Favorite"></i>
                                                                            </button>
                                                                        </td>
                                                                        <td>
                                                                            <Link to={`/database/script/${fav.files.file_id}`} state={{ from: "account" }}>{fav.files["Script Title"]}</Link>
                                                                            {isSmall && (
                                                                                <>
                                                                                    <br />
                                                                                    <small>
                                                                                        {`${fav.files["Writer(s)"]}`}
                                                                                        {fav.files["Draft Date"] ? `, ${fav.files["Draft Date"]}` : ''}
                                                                                    </small>
                                                                                </>
                                                                            )}
                                                                        </td>
                                                                        {!isSmall && (
                                                                            <>
                                                                                <td>{fav.files["Writer(s)"]}</td>
                                                                                <td>{fav.files["Draft Date"] || ""}</td>
                                                                            </>
                                                                        )}
                                                                    </tr>
                                                                )
                                                            })}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            ) : (
                                                isLoadingFavorites ? (
                                                    <p>
                                                        <LoadingText text="Checking for Favorites..." className="mb-0" />
                                                    </p>
                                                ) : (
                                                    <p>Nothing yet! Click the <i className="bi bi-star-fill" style={{ color: "inherit" }}></i> next to scripts in the <Link to="/database">Database</Link> to add them here.</p>
                                                )
                                            )}
                                        </>
                                    ) : (
                                        <p>Become a <Link to="https://patreon.com/scripthive" target="_blank">Gold Level</Link> member on Patreon to access Favorites and other exclusive features!</p>
                                    )}
                                    </>
                                )}
                                {activeTab === "downloads" && (
                                    <>
                                        {userDownloads.length > 0 ? (
                                            <div className="script-table overflow-auto" style={isLarge ? { maxHeight: 'calc(100vh - 210px)' } : {}}>
                                                <p>You've downloaded a total of <strong>{userDownloads.length}</strong> scripts.</p>
                                                <Table striped hover size="sm">
                                                    <thead className="sticky-top">
                                                        <tr>
                                                            <th>Script Title</th>
                                                            {!isSmall && (
                                                                <>
                                                                    <th>Writer(s)</th>
                                                                    <th style={{ width: 120 }}>Downloaded</th>
                                                                </>
                                                            )}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {userDownloads.filter(d => d && d.files).map((download) => {
                                                            return download?.script_id ? (
                                                                <tr key={download.script_id}>
                                                                    <td>
                                                                        <Link to={`/database/script/${download.script_id}`} state={{ from: "account" }}>{download.files["Script Title"]}</Link>
                                                                        {isSmall && (
                                                                            <>
                                                                                <br />
                                                                                <small>
                                                                                    {download.files["Writer(s)"] ? `Written by ${download.files["Writer(s)"]}` : ''}
                                                                                    {download.download_date ? `, downloaded ${formatDate(download.download_date) }` : ''}
                                                                                </small>
                                                                            </>
                                                                        )}
                                                                    </td>
                                                                    {!isSmall && (
                                                                        <>
                                                                            <td>{download.files["Writer(s)"] || ""}</td>
                                                                            <td>{formatDate(download.download_date) || ""}</td>
                                                                        </>
                                                                    )}
                                                                </tr>
                                                            ) : null;
                                                        })}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        ) : (
                                            isLoadingDownloads ? (
                                                <p>
                                                    <LoadingText text="Looking for downloads..." className="mb-0" />
                                                </p>
                                            ) : (
                                                <p>No downloads yet! Start browsing the <Link to="/database">Database</Link> and #ReadTheMovies.</p>
                                            )
                                        )}
                                    </>
                                )}
                                {activeTab === "requests" && (
                                    <>
                                        {userRequests.length > 0 ? (
                                            <div className="overflow-auto" style={isLarge ? { maxHeight: 'calc(100vh - 210px)' } : {}}>
                                                {addedRequests.length > 0 && (
                                                    <>
                                                        <h5>Added Requests</h5>
                                                        <p>You have <strong>{addedRequests.length}</strong> request{addedRequests.length !== 1 ? 's' : ''} that {addedRequests.length !== 1 ? 'were' : 'was'} added to the database.</p>
                                                        <div className="requests-table">
                                                            <Table striped hover size="sm">
                                                                <thead>
                                                                    <tr>
                                                                        <th style={{ width: "35%" }}>Script Title</th>
                                                                        {!isSmall && (
                                                                            <>
                                                                                <th style={{ width: "35%" }}>Writer(s)</th>
                                                                                <th style={{ width: "15%" }}>Requested</th>
                                                                                <th style={{ width: "15%" }}>Added</th>
                                                                            </>
                                                                        )}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {addedRequests.map((req) => {
                                                                        return (
                                                                            <tr key={req.id}>
                                                                                <td>
                                                                                    {req.added_to_database ? (
                                                                                        <Link to={req.database_id ? `/database/script/${req.database_id}` : `/database/?Script+Title=${req.script_title.replaceAll(" ", "+")}`} state={{ from: "account" }}>{req.script_title}</Link>
                                                                                    ) : (
                                                                                        req.script_title
                                                                                    )}
                                                                                    {isSmall && (
                                                                                        <>
                                                                                            <br />
                                                                                            <small>
                                                                                                {req.writers ? req.writers : ''}
                                                                                                {req.date_requested ? `, requested ${formatDate(req.date_requested)}` : ''}
                                                                                                {req.files?.["Date Added"] ? `, added ${formatDate(req.files["Date Added"])}` : ""}
                                                                                            </small>
                                                                                        </>
                                                                                    )}
                                                                                </td>
                                                                                {!isSmall && (
                                                                                    <>
                                                                                        <td>{req.writers || ""}</td>
                                                                                        <td>{formatDate(req.date_requested) || ""}</td>
                                                                                        <td>{req.files?.["Date Added"] ? formatDate(req.files["Date Added"]) : ""}</td>
                                                                                    </>
                                                                                )}
                                                                            </tr>
                                                                        )
                                                                    })}
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    </>
                                                )}
                                                {pendingRequests.length > 0 && (
                                                    <>
                                                        <h5>Pending Requests</h5>
                                                        <p>You have <strong>{pendingRequests.length}</strong> request{pendingRequests.length !== 1 ? 's' : ''} that {pendingRequests.length !== 1 ? 'are' : 'is'} currently pending.</p>
                                                        <div className="requests-table">
                                                            <Table striped hover size="sm">
                                                                <thead>
                                                                    <tr>
                                                                        <th style={{ width: "35%" }}>Script Title</th>
                                                                        {!isSmall && (
                                                                            <>
                                                                                <th style={{ width: "35%" }}>Writer(s)</th>
                                                                                <th style={{ width: "15%" }}>Requested</th>
                                                                                <th style={{ width: "15%" }}>Actions</th>
                                                                            </>
                                                                        )}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {pendingRequests.map((req) => {
                                                                        return (
                                                                            <tr key={req.id}>
                                                                                <td>
                                                                                    {req.script_title}
                                                                                    {isSmall && (
                                                                                        <>
                                                                                            <br />
                                                                                            <small>
                                                                                                {req.writers ? req.writers : ''}
                                                                                                {req.date_requested ? `, requested ${formatDate(req.date_requested)}` : ''}
                                                                                            </small>
                                                                                        </>
                                                                                    )}
                                                                                </td>
                                                                                {!isSmall && (
                                                                                    <>
                                                                                        <td>{req.writers || ""}</td>
                                                                                        <td>{formatDate(req.date_requested) || ""}</td>
                                                                                    </>
                                                                                )}
                                                                                <td>
                                                                                    <Button
                                                                                        variant="outline-danger"
                                                                                        size="sm"
                                                                                        onClick={() => {
                                                                                            setConfirmationMessage(`Are you sure you want to delete the request for ${req.script_title}${req.writers ? ` by ${req.writers}` : ''}?\n\nDeleted requests for the same script title and writers cannot be resubmitted.`);
                                                                                            setOnDeleteConfirm(() => () => {
                                                                                                deleteRequest(req).then(() => {
                                                                                                getUserRequests(userData.user_id).then((data) => {
                                                                                                if (data) {
                                                                                                    setUserRequests(data);
                                                                                                }
                                                                                                });
                                                                                            });
                                                                                            setShowDeleteModal(false);
                                                                                            });
                                                                                            setOnDeleteHide(() => () => {
                                                                                            setShowDeleteModal(false);
                                                                                            });
                                                                                            setShowDeleteModal(true);
                                                                                        }}
                                                                                    >
                                                                                        Delete
                                                                                    </Button>
                                                                                </td>
                                                                            </tr>
                                                                        )
                                                                    })}
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        ) : (
                                            isLoadingRequests ? (
                                                <p>
                                                    <LoadingText text="Checking for Requests..." className="mb-0" />
                                                </p>
                                            ) : (
                                                <p>Looking for a script that we don't currently have in our database? Submit a <Link to="/requests">request</Link>!</p>
                                            )
                                        )}
                                    </>
                                )}
                                {activeTab === "indexed" && (
                                    <>
                                    {userData?.isIndexer && (
                                        <>
                                            {userIndexed.length > 0 ? (
                                                <div className="script-table overflow-auto" style={isLarge ? { maxHeight: 'calc(100vh - 210px)' } : {}}>
                                                    <Table striped hover size="sm">
                                                        <thead className="sticky-top">
                                                            <tr>
                                                                <th>Script Title</th>
                                                                {!isSmall && (
                                                                    <>
                                                                        <th>Writer(s)</th>
                                                                        <th>Date Added</th>
                                                                    </>
                                                                )}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {userIndexed.map((idx) => {
                                                                return idx?.file_id ? (
                                                                    <tr key={idx.file_id}>
                                                                        <td>
                                                                            <Link to={`/database/script/${idx.file_id}`} state={{ from: "account" }}>{idx["Script Title"]}</Link>
                                                                            {isSmall && (
                                                                                <>
                                                                                    <br />
                                                                                    <small>
                                                                                        {idx["Writer(s)"] ? `Written by ${idx["Writer(s)"]}` : ''}
                                                                                        {idx["Date Added"] ? `, added ${idx["Date Added"]}` : ''}
                                                                                    </small>
                                                                                </>
                                                                            )}
                                                                        </td>
                                                                        {!isSmall && (
                                                                            <>
                                                                                <td>{idx["Writer(s)"] || ""}</td>
                                                                                <td>{idx["Date Added"] || ""}</td>
                                                                            </>
                                                                        )}
                                                                    </tr>
                                                                ) : null;
                                                            })}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            ) : (
                                                isLoadingIndexed ? (
                                                    <p>
                                                        <LoadingText text="Looking for Indexed files..." className="mb-0" />
                                                    </p>
                                                ) : (
                                                    <p>Nothing yet! Start <Link to="/indexerDashboard">Indexing</Link> files to add them to the <Link to="/database">Database</Link>.</p>
                                                )
                                            )}
                                        </>
                                    )}
                                    </>
                                )}
                            </Row>
                        </Col>
                    </Row>
                </Container>
            </div>
            <ConfirmationModal show={showDeleteModal} message={confirmationMessage} onConfirm={onDeleteConfirm} onHide={onDeleteHide} />
            <DeleteAccountModal
                show={showDeleteAccountModal}
                onHide={() => setShowDeleteAccountModal(false)}
                onConfirm={async () => {
                    const success = await deleteAccount();
                    if (success) {
                        await supabase.auth.signOut();
                        navigate('/');
                        showToast('info', 'Your account has been permanently deleted.');
                    } else {
                        showToast('error', 'Failed to delete account. Please contact support.');
                        setShowDeleteAccountModal(false);
                    }
                }}
            />
        </div>
    );
};

export default AccountPage;
